<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import axios from 'axios'
import { useRouter } from 'vue-router'
import TeamSearchInput from './TeamSearchInput.vue'
import DateTimePickerCustom from './DateTimePickerCustom.vue'
import DateRangePicker from './DateRangePicker.vue'
import { useUserStore } from '../stores/userStore'
import { useMenuStore } from '../stores/menuStore'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'close'])
const router = useRouter()
const userStore = useUserStore()
const menuStore = useMenuStore()

const leagues = ref([])
const fixtures = ref([])
const selectedLeagueId = ref(null)
const loadingLeagues = ref(false)
const loadingFixtures = ref(false)
const leaguesError = ref(null)
const fixturesError = ref(null)
const searchLeague = ref('')
const expandedDates = ref(new Set())

// Fixture creation form state
const formData = ref({
  datetime: '',
  homeTeam: '',
  awayTeam: '',
  homeTeamId: null,
  awayTeamId: null
})
const isSubmitting = ref(false)
const createFixtureError = ref(null)
const showCreateForm = ref(false)

// Date range filtering
const fixturesRange = ref('next3')
const customRange = ref({
  start: '',
  end: ''
})
const showRangeDropdown = ref(false)

const filteredLeagues = computed(() => {
  if (!searchLeague.value.trim()) {
    return leagues.value
  }
  const search = searchLeague.value.toLowerCase()
  return leagues.value.filter(league => 
    league.league_name?.toLowerCase().includes(search) ||
    league.slug?.toLowerCase().includes(search)
  )
})

const selectedLeague = computed(() => {
  return leagues.value.find(l => l.id === selectedLeagueId.value) || null
})

const fetchLeagues = async () => {
  loadingLeagues.value = true
  leaguesError.value = null
  try {
    const response = await axios.get('/api/leagues/get-leagues')
    leagues.value = response.data || []
  } catch (error) {
    leaguesError.value = 'Failed to load leagues'
    console.error('Error fetching leagues:', error)
  } finally {
    loadingLeagues.value = false
  }
}

const fetchFixtures = async (leagueId) => {
  if (!leagueId) {
    fixtures.value = []
    return
  }
  
  loadingFixtures.value = true
  fixturesError.value = null
  try {
    // Calculate date range based on current filter
    const today = new Date()
    let startDate = new Date()
    let endDate = new Date()

    switch(fixturesRange.value) {
      case 'prev7':
      case 'prev14':
      case 'prev30':
      case 'prev90':
        const days = parseInt(fixturesRange.value.replace('prev', ''))
        startDate.setDate(today.getDate() - days)
        endDate = today
        break
      case 'next7':
      case 'next14':
      case 'next30':
        const nextDays = parseInt(fixturesRange.value.replace('next', ''))
        startDate = today
        endDate.setDate(today.getDate() + nextDays)
        break
      case 'today':
        startDate = today
        endDate = today
        break
      case 'tomorrow':
        startDate.setDate(today.getDate() + 1)
        endDate.setDate(today.getDate() + 1)
        break
      case 'next3':
        startDate = today
        endDate.setDate(today.getDate() + 3)
        break
      case 'custom':
        if (customRange.value.start && customRange.value.end) {
          startDate = new Date(customRange.value.start)
          endDate = new Date(customRange.value.end)
        }
        break
      default:
        // Default to next 3 days
        startDate = today
        endDate.setDate(today.getDate() + 3)
    }

    // Format dates for API
    const formattedStartDate = startDate.toISOString().split('T')[0]
    const formattedEndDate = endDate.toISOString().split('T')[0]

    // Build API URL with date parameters
    let apiUrl = `/api/v2/scheduled-games-by-league/${leagueId}`
    const params = new URLSearchParams({
      date_from: formattedStartDate,
      date_to: formattedEndDate
    })
    apiUrl += `?${params.toString()}`

    const response = await axios.get(apiUrl)
    fixtures.value = response.data || []
  } catch (error) {
    fixturesError.value = 'Failed to load fixtures'
    console.error('Error fetching fixtures:', error)
  } finally {
    loadingFixtures.value = false
  }
}

const selectLeague = (league) => {
  selectedLeagueId.value = league.id
  fetchFixtures(league.id)
}

const getTeamName = (team) => {
  if (!team) return '—'
  return team.team_name || team.name || `Team ${team.id || 'Unknown'}`
}

const formatGameDate = (dateValue) => {
  if (!dateValue) return '—'
  try {
    // Handle if it's already formatted (string) or needs formatting (Date object)
    if (typeof dateValue === 'string') {
      // Check if it's already in a formatted state like "d.m.Y H:i"
      const date = new Date(dateValue)
      if (isNaN(date.getTime())) {
        // Already formatted, return as is
        return dateValue
      }
      // Parse and format
      return date.toLocaleString('en-GB', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
    const date = new Date(dateValue)
    return date.toLocaleString('en-GB', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch (e) {
    return String(dateValue)
  }
}

const getDateFromGamedate = (gamedate) => {
  if (!gamedate) return null
  try {
    // Handle if gamedate is already in DD.MM.YYYY format (with or without time)
    if (typeof gamedate === 'string' && gamedate.match(/^\d{2}\.\d{2}\.\d{4}/)) {
      const datePart = gamedate.split(' ')[0] // Get date part before space (removes time)
      const [day, month, year] = datePart.split('.')
      // Return as YYYY-MM-DD for grouping key (ensures proper sorting)
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
    }
    
    // Try parsing as standard date
    const date = new Date(gamedate)
    if (!isNaN(date.getTime())) {
      // Return date in format YYYY-MM-DD for grouping (for proper sorting)
      const year = date.getFullYear()
      const month = (date.getMonth() + 1).toString().padStart(2, '0')
      const day = date.getDate().toString().padStart(2, '0')
      return `${year}-${month}-${day}`
    }
    
    return null
  } catch (e) {
    return null
  }
}

const formatDateHeader = (dateStr) => {
  if (!dateStr) return '—'
  try {
    // Handle if dateStr is already in DD.MM.YYYY format
    if (typeof dateStr === 'string' && dateStr.match(/^\d{2}\.\d{2}\.\d{4}/)) {
      return dateStr.split(' ')[0] // Return just the date part
    }
    // Handle YYYY-MM-DD format (from grouping key)
    let date
    if (dateStr.includes('T')) {
      date = new Date(dateStr)
    } else {
      date = new Date(dateStr + 'T00:00:00')
    }
    if (isNaN(date.getTime())) {
      // Try parsing as DD.MM.YYYY
      const parts = dateStr.split('.')
      if (parts.length === 3) {
        const [day, month, year] = parts
        date = new Date(`${year}-${month}-${day}`)
      }
      if (isNaN(date.getTime())) return dateStr
    }
    const day = date.getDate().toString().padStart(2, '0')
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const year = date.getFullYear()
    return `${day}.${month}.${year}`
  } catch (e) {
    return dateStr
  }
}

const formatDateTime = (dateValue) => {
  if (!dateValue) return '—'
  try {
    if (typeof dateValue === 'string') {
      // Check if already formatted like "d.m.Y H:i"
      if (dateValue.match(/^\d{2}\.\d{2}\.\d{4} \d{2}:\d{2}$/)) {
        return dateValue
      }
      const date = new Date(dateValue)
      if (isNaN(date.getTime())) return dateValue
      return date.toLocaleDateString('en-GB', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
    const date = new Date(dateValue)
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch (e) {
    return String(dateValue)
  }
}

const fixturesByDate = computed(() => {
  const grouped = {}
  fixtures.value.forEach(fixture => {
    const dateKey = getDateFromGamedate(fixture.gamedate)
    if (!dateKey) return
    
    if (!grouped[dateKey]) {
      grouped[dateKey] = []
    }
    grouped[dateKey].push(fixture)
  })
  
  // Sort dates
  const sortedDates = Object.keys(grouped).sort()
  const result = {}
  sortedDates.forEach(date => {
    result[date] = grouped[date]
  })
  
  return result
})

const toggleDateGroup = (dateKey) => {
  if (expandedDates.value.has(dateKey)) {
    expandedDates.value.delete(dateKey)
  } else {
    expandedDates.value.add(dateKey)
  }
}

const openFixtureInNewTab = (fixtureId, event) => {
  event.stopPropagation()
  window.open(`/fixture/${fixtureId}`, '_blank')
}

const quickRanges = [
  { value: 'today', label: 'Today' },
  { value: 'tomorrow', label: 'Tomorrow' },
  { value: 'next3', label: 'Next 3' },
  { value: 'next7', label: 'Next 7' },
]

const rangeOptions = [
  { value: 'prev7', label: 'Last 7 days' },
  { value: 'prev14', label: 'Last 14 days' },
  { value: 'prev30', label: 'Last 30 days' },
  { value: 'prev90', label: 'Last 3 months' },
  { value: 'next7', label: 'Next 7 days' },
  { value: 'next14', label: 'Next 14 days' },
  { value: 'next30', label: 'Next 30 days' },
  { value: 'custom', label: 'Custom Range' }
]

const getRangeText = computed(() => {
  if (fixturesRange.value === 'custom' && customRange.value.start && customRange.value.end) {
    const startDate = new Date(customRange.value.start)
    const endDate = new Date(customRange.value.end)
    return `${formatDateEst(startDate)} - ${formatDateEst(endDate)}`
  }
  return rangeOptions.find(opt => opt.value === fixturesRange.value)?.label || 
         quickRanges.find(r => r.value === fixturesRange.value)?.label || 
         'Select Range'
})

const formatDateEst = (date) => {
  const day = date.getDate().toString().padStart(2, '0')
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const year = date.getFullYear()
  return `${day}.${month}.${year}`
}

const selectRange = (value) => {
  fixturesRange.value = value
  if (value !== 'custom') {
    showRangeDropdown.value = false
    // Update customRange to show in calendar
    const today = new Date()
    let startDate = new Date()
    let endDate = new Date()

    switch(value) {
      case 'prev7':
      case 'prev14':
      case 'prev30':
      case 'prev90':
        const days = parseInt(value.replace('prev', ''))
        startDate.setDate(today.getDate() - days)
        endDate = today
        break
      case 'next7':
      case 'next14':
      case 'next30':
        const nextDays = parseInt(value.replace('next', ''))
        startDate = today
        endDate.setDate(today.getDate() + nextDays)
        break
      case 'today':
        startDate = today
        endDate = today
        break
      case 'tomorrow':
        startDate.setDate(today.getDate() + 1)
        endDate.setDate(today.getDate() + 1)
        break
      case 'next3':
        startDate = today
        endDate.setDate(today.getDate() + 3)
        break
    }

    customRange.value = {
      start: startDate.toISOString().split('T')[0],
      end: endDate.toISOString().split('T')[0]
    }

    if (selectedLeagueId.value) {
      fetchFixtures(selectedLeagueId.value)
    }
  }
}

const applyCustomRange = () => {
  if (customRange.value.start && customRange.value.end) {
    fixturesRange.value = 'custom'
    if (selectedLeagueId.value) {
      fetchFixtures(selectedLeagueId.value)
    }
    showRangeDropdown.value = false
  }
}

const handleClickOutside = (e) => {
  const dropdown = e.target.closest('.range-dropdown')
  const isClickInside = dropdown !== null
  if (!isClickInside) {
    showRangeDropdown.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})

watch(fixturesRange, (newRange) => {
  if (newRange !== 'custom' && selectedLeagueId.value) {
    const today = new Date()
    let startDate = new Date()
    let endDate = new Date()

    switch(newRange) {
      case 'prev7':
      case 'prev14':
      case 'prev30':
      case 'prev90':
        const days = parseInt(newRange.replace('prev', ''))
        startDate.setDate(today.getDate() - days)
        endDate = today
        break
      case 'next7':
      case 'next14':
      case 'next30':
        const nextDays = parseInt(newRange.replace('next', ''))
        startDate = today
        endDate.setDate(today.getDate() + nextDays)
        break
      case 'today':
        startDate = today
        endDate = today
        break
      case 'tomorrow':
        startDate.setDate(today.getDate() + 1)
        endDate.setDate(today.getDate() + 1)
        break
      case 'next3':
        startDate = today
        endDate.setDate(today.getDate() + 3)
        break
    }

    customRange.value = {
      start: startDate.toISOString().split('T')[0],
      end: endDate.toISOString().split('T')[0]
    }
  }
})

watch(customRange, (newRange) => {
  if (fixturesRange.value === 'custom' && newRange.start && newRange.end && selectedLeagueId.value) {
    fetchFixtures(selectedLeagueId.value)
  }
}, { deep: true })

const handleTeamSelect = (team, type) => {
  formData.value[`${type}TeamId`] = team.id
  formData.value[`${type}Team`] = team.team_name || team.name || ''
}

const handleCreateFixture = async () => {
  if (!selectedLeagueId.value) {
    createFixtureError.value = 'Please select a league first'
    return
  }

  isSubmitting.value = true
  createFixtureError.value = null

  try {
    // Split datetime into date and time
    const [date, time] = formData.value.datetime.split(', ')

    // Convert local datetime to UTC
    let date_time_utc = ''
    
    if (date && time) {
      // Parse local date and time (format: "dd.mm.yyyy, HH:mm")
      const [day, month, year] = date.split('.')
      const [hour, minute] = time.split(':')
      
      // Create Date object in local timezone
      const localDate = new Date(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day),
        parseInt(hour),
        parseInt(minute)
      )
      
      // Convert to UTC and format as ISO 8601: "YYYY-MM-DDTHH:mm:ssZ"
      const utcYear = localDate.getUTCFullYear()
      const utcMonth = (localDate.getUTCMonth() + 1).toString().padStart(2, '0')
      const utcDay = localDate.getUTCDate().toString().padStart(2, '0')
      const utcHour = localDate.getUTCHours().toString().padStart(2, '0')
      const utcMinute = localDate.getUTCMinutes().toString().padStart(2, '0')
      const utcSecond = localDate.getUTCSeconds().toString().padStart(2, '0')
      
      // Format UTC datetime as ISO 8601: "YYYY-MM-DDTHH:mm:ssZ"
      date_time_utc = `${utcYear}-${utcMonth}-${utcDay}T${utcHour}:${utcMinute}:${utcSecond}Z`
    }

    const payload = {
      date,
      time,
      date_time_utc,
      home_team_id: formData.value.homeTeamId,
      away_team_id: formData.value.awayTeamId,
      league_id: selectedLeagueId.value
    }

    const { data } = await axios.post('/api/fixtures/generate', payload)
    
    // Handle successful response
    console.log('Fixture generated:', data)
    
    // Refresh fixtures list
    await fetchFixtures(selectedLeagueId.value)
    
    // Reset form
    formData.value = {
      datetime: '',
      homeTeam: '',
      awayTeam: '',
      homeTeamId: null,
      awayTeamId: null
    }
    showCreateForm.value = false
  } catch (err) {
    createFixtureError.value = err.response?.data?.message || 'An error occurred while generating the fixture'
    console.error('Error generating fixture:', err)
  } finally {
    isSubmitting.value = false
  }
}

const openFixture = (fixtureId) => {
  router.push(`/fixture/${fixtureId}`)
  closeOverlay()
}

const closeOverlay = () => {
  emit('update:modelValue', false)
  emit('close')
}

watch(() => props.modelValue, (isOpen) => {
  if (isOpen) {
    fetchLeagues()
    selectedLeagueId.value = null
    fixtures.value = []
    searchLeague.value = ''
    expandedDates.value = new Set()
    showCreateForm.value = menuStore.openCreateFixtureForm
    menuStore.openCreateFixtureForm = false
    showRangeDropdown.value = false
    fixturesRange.value = 'next3'
    formData.value = {
      datetime: '',
      homeTeam: '',
      awayTeam: '',
      homeTeamId: null,
      awayTeamId: null
    }
    createFixtureError.value = null
    // Initialize date range
    const today = new Date()
    const endDate = new Date()
    endDate.setDate(today.getDate() + 3)
    customRange.value = {
      start: today.toISOString().split('T')[0],
      end: endDate.toISOString().split('T')[0]
    }
  }
})

watch(fixtures, () => {
  // Auto-expand all date groups when fixtures are loaded
  Object.keys(fixturesByDate.value).forEach(dateKey => {
    expandedDates.value.add(dateKey)
  })
}, { immediate: true })

watch(selectedLeagueId, () => {
  formData.value = {
    datetime: '',
    homeTeam: '',
    awayTeam: '',
    homeTeamId: null,
    awayTeamId: null
  }
  createFixtureError.value = null
})
</script>

<template>
  <div 
    v-if="modelValue" 
    class="fixed inset-0 bg-black/60 z-[9998] flex items-center justify-center p-4"
    data-no-global-context-menu
  >
    <div class="absolute inset-0" @click="closeOverlay"></div>
    <div class="relative z-10 bg-white rounded-lg shadow-2xl w-full max-w-[95vw] h-[90vh] flex flex-col">
      <!-- Header -->
      <div class="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <div class="flex items-center space-x-3">
          <i class="fas fa-calendar-alt text-pink-500"></i>
          <div>
            <div class="text-base font-semibold text-gray-800">Leagues & Fixtures</div>
            <div class="text-xs text-gray-500">Select a league to view scheduled fixtures</div>
          </div>
        </div>
        <button 
          class="p-2 rounded-full hover:bg-gray-100 text-gray-500" 
          @click="closeOverlay"
          title="Close"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Content -->
      <div class="flex flex-1 min-h-0 divide-x divide-gray-200">
        <!-- Left: Leagues List -->
        <div class="w-1/3 flex flex-col min-h-0">
          <div class="p-4 border-b border-gray-200">
            <input 
              v-model="searchLeague"
              type="text"
              class="w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-pink-500"
              placeholder="Search leagues..."
            />
          </div>
          <div class="flex-1 overflow-auto p-4">
            <div v-if="loadingLeagues" class="text-sm text-gray-500 text-center py-4">
              <i class="fas fa-spinner fa-spin mr-2"></i> Loading leagues...
            </div>
            <div v-else-if="leaguesError" class="text-sm text-red-500 text-center py-4">
              {{ leaguesError }}
            </div>
            <div v-else-if="!filteredLeagues.length" class="text-sm text-gray-500 text-center py-4">
              {{ searchLeague ? 'No leagues found' : 'No leagues available' }}
            </div>
            <ul v-else class="space-y-1">
              <li
                v-for="league in filteredLeagues"
                :key="league.id"
                class="px-3 py-2 rounded cursor-pointer transition-colors"
                :class="selectedLeagueId === league.id 
                  ? 'bg-pink-500 text-white' 
                  : 'hover:bg-pink-50 text-gray-700'"
                @click="selectLeague(league)"
              >
                <div class="font-medium">{{ league.league_name || league.slug || `League ${league.id}` }}</div>
                <div 
                  class="text-xs"
                  :class="selectedLeagueId === league.id ? 'text-pink-100' : 'text-gray-500'"
                >
                  {{ league.slug || '—' }}
                </div>
              </li>
            </ul>
          </div>
        </div>

        <!-- Right: Fixtures List -->
        <div class="w-2/3 flex flex-col min-h-0">
          <div class="p-4 border-b border-gray-200 space-y-4">
            <div class="flex items-center justify-between">
              <div class="text-sm font-semibold text-gray-700">
                <span v-if="selectedLeague">
                  Scheduled Fixtures: {{ selectedLeague.league_name }}
                </span>
                <span v-else class="text-gray-500">
                  Select a league to view fixtures
                </span>
              </div>
              <button
                v-if="selectedLeagueId && userStore.currentUser?.role === 'admin'"
                @click="showCreateForm = !showCreateForm"
                class="px-3 py-1.5 text-sm bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors flex items-center gap-2"
              >
                <i class="fas fa-plus"></i>
                {{ showCreateForm ? 'Hide Form' : 'Create Fixture' }}
              </button>
            </div>

            <!-- Date Range Filter (only show when league is selected) -->
            <div v-if="selectedLeagueId" class="flex items-center gap-6">
              <div class="flex items-center gap-3">
                <i class="fas fa-clock text-pink-500"></i>
                <span class="text-sm text-gray-700">Recent Fixtures</span>
              </div>
              
              <!-- Quick select buttons -->
              <div class="flex gap-2">
                <button
                  v-for="range in quickRanges"
                  :key="range.value"
                  @click="selectRange(range.value)"
                  class="px-3 py-1.5 text-sm rounded-full transition-colors"
                  :class="fixturesRange === range.value ? 
                    'bg-pink-500 text-white' : 
                    'bg-gray-100 text-gray-600 hover:bg-gray-200'"
                >
                  {{ range.label }}
                </button>
              </div>

              <!-- Range dropdown -->
              <div class="relative range-dropdown">
                <div 
                  @click="showRangeDropdown = !showRangeDropdown"
                  class="flex bg-white items-center gap-2 px-4 py-2 border rounded-lg cursor-pointer hover:bg-gray-50 text-sm"
                >
                  <i class="fas fa-calendar text-gray-400"></i>
                  <span class="text-gray-700">{{ getRangeText }}</span>
                  <i class="fas fa-chevron-down text-gray-400 text-xs ml-2"></i>
                </div>

                <div 
                  v-if="showRangeDropdown" 
                  class="absolute top-full left-0 mt-2 bg-white rounded-lg shadow-lg border p-4 min-w-[900px] z-10 grid grid-cols-[300px_1fr] gap-6"
                  @click.stop
                >
                  <!-- Left side - Options -->
                  <div class="border-r pr-4">
                    <!-- Previous days section -->
                    <div class="mb-6">
                      <div class="px-2 py-2 text-xs font-medium text-gray-500">PREVIOUS</div>
                      <div 
                        v-for="option in rangeOptions.filter(opt => opt.value.startsWith('prev'))"
                        :key="option.value"
                        @click="selectRange(option.value)"
                        class="px-4 py-2 hover:bg-gray-50 cursor-pointer text-sm flex items-center gap-2 rounded"
                        :class="{ 'text-pink-500 bg-pink-50': fixturesRange === option.value }"
                      >
                        <i class="fas fa-check text-xs" v-if="fixturesRange === option.value"></i>
                        <span :class="{ 'ml-4': fixturesRange !== option.value }">{{ option.label }}</span>
                      </div>
                    </div>

                    <!-- Next days section -->
                    <div class="mb-6">
                      <div class="px-2 py-2 text-xs font-medium text-gray-500">NEXT</div>
                      <div 
                        v-for="option in rangeOptions.filter(opt => opt.value.startsWith('next'))"
                        :key="option.value"
                        @click="selectRange(option.value)"
                        class="px-4 py-2 hover:bg-gray-50 cursor-pointer text-sm flex items-center gap-2 rounded"
                        :class="{ 'text-pink-500 bg-pink-50': fixturesRange === option.value }"
                      >
                        <i class="fas fa-check text-xs" v-if="fixturesRange === option.value"></i>
                        <span :class="{ 'ml-4': fixturesRange !== option.value }">{{ option.label }}</span>
                      </div>
                    </div>

                    <!-- Custom range option -->
                    <div>
                      <div 
                        @click="selectRange('custom')"
                        class="px-4 py-2 hover:bg-gray-50 cursor-pointer text-sm flex items-center gap-2 rounded"
                        :class="{ 'text-pink-500 bg-pink-50': fixturesRange === 'custom' }"
                      >
                        <i class="fas fa-check text-xs" v-if="fixturesRange === 'custom'"></i>
                        <span :class="{ 'ml-4': fixturesRange !== 'custom' }">Custom Range</span>
                      </div>
                    </div>
                  </div>

                  <!-- Right side - Calendar -->
                  <div>
                    <DateRangePicker
                      v-model="customRange"
                      @apply="applyCustomRange"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Create Fixture Form -->
          <div v-if="showCreateForm && selectedLeagueId && userStore.currentUser?.role === 'admin'" class="p-4 bg-gray-50 border-b border-gray-200">
            <div class="bg-white rounded-lg p-6 shadow-sm">
              <h3 class="text-lg font-semibold mb-4">Create New Fixture</h3>
              
              <div class="space-y-6">
                <div>
                  <label class="block text-sm text-gray-500 mb-1">Match Date & Time</label>
                  <DateTimePickerCustom
                    v-model="formData.datetime"
                    placeholder="Select date and time"
                  />
                </div>

                <div class="grid grid-cols-[1fr_auto_1fr] gap-6 items-end">
                  <div>
                    <label class="block text-sm text-gray-500 mb-1">Home Team</label>
                    <TeamSearchInput
                      v-model="formData.homeTeam"
                      placeholder="Search home team..."
                      @select="(team) => handleTeamSelect(team, 'home')"
                    />
                  </div>
                  <div class="flex items-center justify-center pb-2">
                    <span class="text-lg font-medium text-gray-500">VS</span>
                  </div>
                  <div>
                    <label class="block text-sm text-gray-500 mb-1">Away Team</label>
                    <TeamSearchInput
                      v-model="formData.awayTeam"
                      placeholder="Search away team..."
                      @select="(team) => handleTeamSelect(team, 'away')"
                    />
                  </div>
                </div>

                <!-- Error message -->
                <div v-if="createFixtureError" class="text-red-500 text-sm mt-2">
                  {{ createFixtureError }}
                </div>

                <!-- Submit Button -->
                <div class="flex justify-end mt-6">
                  <button 
                    @click="handleCreateFixture"
                    :disabled="isSubmitting || !formData.datetime || !formData.homeTeamId || !formData.awayTeamId"
                    class="px-6 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    <span v-if="isSubmitting" class="mr-2">
                      <svg class="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </span>
                    {{ isSubmitting ? 'Generating...' : 'Generate Fixture' }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="flex-1 overflow-auto p-4">
            <div v-if="!selectedLeagueId" class="text-sm text-gray-500 text-center py-8">
              <i class="fas fa-hand-pointer text-4xl mb-3 text-gray-300"></i>
              <p>Please select a league from the left panel</p>
            </div>
            <div v-else-if="loadingFixtures" class="text-sm text-gray-500 text-center py-4">
              <i class="fas fa-spinner fa-spin mr-2"></i> Loading fixtures...
            </div>
            <div v-else-if="fixturesError" class="text-sm text-red-500 text-center py-4">
              {{ fixturesError }}
            </div>
            <div v-else-if="!fixtures.length" class="text-sm text-gray-500 text-center py-4">
              No scheduled fixtures found for this league
            </div>
            <div v-else class="space-y-3">
              <div
                v-for="(dateFixtures, dateKey) in fixturesByDate"
                :key="dateKey"
                class="border border-gray-200 rounded overflow-hidden"
              >
                <!-- Date Header (Collapsible) -->
                <button
                  @click="toggleDateGroup(dateKey)"
                  class="w-full px-4 py-3 bg-gray-700 text-white flex items-center justify-between hover:bg-gray-600 transition-colors"
                >
                  <div class="flex items-center gap-2">
                    <i class="fas fa-calendar"></i>
                    <span class="font-semibold">{{ formatDateHeader(dateKey) }} ({{ dateFixtures.length }} {{ dateFixtures.length === 1 ? 'game' : 'games' }})</span>
                  </div>
                  <i 
                    class="fas transition-transform"
                    :class="expandedDates.has(dateKey) ? 'fa-chevron-up' : 'fa-chevron-down'"
                  ></i>
                </button>
                
                <!-- Fixtures Table (Collapsible) -->
                <div v-if="expandedDates.has(dateKey)" class="overflow-x-auto">
                  <table class="min-w-full text-xs">
                    <thead class="bg-gray-100 text-gray-700 uppercase">
                      <tr>
                        <th class="px-3 py-2 text-left">FIXTURE ID</th>
                        <th class="px-3 py-2 text-left">TIME</th>
                        <th class="px-3 py-2 text-left">TEAMS</th>
                        <th class="px-3 py-2 text-left">UPDATED AT</th>
                        <th class="px-3 py-2 text-left">CREATED BY</th>
                        <th class="px-3 py-2 text-left">PUBLISHED AT</th>
                        <th class="px-3 py-2 text-left">ACTIONS</th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-100">
                      <tr
                        v-for="fixture in dateFixtures"
                        :key="fixture.id"
                        class="hover:bg-gray-50 cursor-pointer"
                        @click="openFixture(fixture.id)"
                      >
                        <td class="px-3 py-2">
                          <div class="flex items-center gap-2">
                            <span class="font-semibold">#{{ fixture.id }}</span>
                            <button
                              @click="openFixtureInNewTab(fixture.id, $event)"
                              class="text-pink-500 hover:text-pink-700 flex items-center gap-1 text-xs"
                              title="Open in new tab"
                            >
                              <span>New Tab</span>
                              <i class="fas fa-external-link-alt text-[10px]"></i>
                            </button>
                          </div>
                        </td>
                        <td class="px-3 py-2" @click.stop>
                          <div class="flex items-center gap-2">
                            <span>{{ formatDateTime(fixture.gamedate) }}</span>
                            <button
                              @click.stop
                              class="text-gray-400 hover:text-gray-600"
                              title="Edit time"
                            >
                              <i class="fas fa-edit text-xs"></i>
                            </button>
                          </div>
                        </td>
                        <td class="px-3 py-2">
                          <span class="font-medium">{{ getTeamName(fixture.away_team) }} vs {{ getTeamName(fixture.home_team) }}</span>
                        </td>
                        <td class="px-3 py-2 text-gray-600">
                          {{ formatDateTime(fixture.updated_at || fixture.formatted_updated_at) }}
                        </td>
                        <td class="px-3 py-2 text-gray-600">
                          {{ fixture.user?.name || fixture.created_by || '—' }}
                        </td>
                        <td class="px-3 py-2">
                          <span v-if="fixture.published_at" class="text-green-600">
                            {{ formatDateTime(fixture.published_at) }}
                          </span>
                          <span v-else class="text-gray-400">—</span>
                        </td>
                        <td class="px-3 py-2" @click.stop>
                          <button
                            @click.stop
                            class="text-red-400 hover:text-red-600"
                            title="Delete fixture"
                          >
                            <i class="fas fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
